import fs from 'fs';
import path from 'path';

import { getFirstExternalSourceForPod } from './getFirstExternalSourceForPod';
import { getNodeModuleName } from './getNodeModuleName';
import { getPackageJsonForPath } from './getPackageJsonForPath';
import { parsePodfileLock, PodfileLock } from './parsePodfileLock';

/**
 * A utility for tracing dependencies from a Podfile.lock.
 */
export class PodfileTracer {
  static create(projectRoot: string, { xcodeProject }: { xcodeProject?: { name: string } } = {}) {
    const podfileLock = path.join(projectRoot, 'ios', 'Podfile.lock');
    const podfileContents = fs.readFileSync(podfileLock, 'utf8');
    const rootTargetName = xcodeProject?.name.match(/.*\/(.*)\.\w+/)?.[1] || '';
    const formatter = new PodfileTracer({
      projectRoot,
      rootTargetName,
      podfile: parsePodfileLock(podfileContents) ?? {},
    });

    return formatter;
  }

  get podfile() {
    return this.props.podfile || {};
  }

  constructor(
    public props: {
      projectRoot: string;
      rootTargetName?: string;
      podfile: PodfileLock;
    }
  ) {}

  // Wrap the expensive method in a cache
  getNodeModuleNameForTarget = memoize(this.getNodeModuleNameForTargetWithoutCache.bind(this));

  getNodeModuleNameForTargetWithoutCache(
    target: string
  ): { name: string; isRootTarget: boolean } | null {
    if (!target) {
      return null;
    }

    // Check the list of known pods that are hardcoded into the system.
    if (target in knownPackages) {
      return { name: knownPackages[target], isRootTarget: false };
    }

    // Check if the target matches the root project.
    if (this.isRootTarget(target)) {
      // Get the root package.json
      const pkg = this.getPackageJsonAnyFilePathInModule({
        target,
        filePath: this.props.projectRoot,
      });
      return pkg ? { name: pkg.name, isRootTarget: true } : null;
    }

    // Otherwise, start tracing for dependencies.

    let source = this.getExternalSourceForPod(target);
    if (!source) {
      // Some modules are formatted incorrectly in Xcode like `EXUpdates-EXUpdates` or `EXConstants-EXConstants`
      // here we'll attempt to split the value, ensure there's more than one copy, and that all copies are the same, then we'll check against that new value.
      const parts = target.split('-');
      if (!!parts[0] && parts.length > 1 && parts.every(s => s === parts[0])) {
        source = this.getExternalSourceForPod(parts[0]);
      }
    }

    if (source?.source) {
      // Finally attempt to trace the podspec file.
      const pkg = this.getPackageJsonAnyFilePathInModule({
        target: source.pod,
        filePath: source.source,
      });
      if (pkg) {
        return { name: pkg.name, isRootTarget: false };
      }
    }

    return null;
  }

  isRootTarget(target: string) {
    return (
      this.props.rootTargetName &&
      (target === this.props.rootTargetName || target === `Pods-${this.props.rootTargetName}`)
    );
  }

  getNodeModuleName(
    filePath: string,
    target?: string
  ): { name: string; isRootTarget: boolean } | null {
    const moduleName = getNodeModuleName(filePath);

    if (moduleName) {
      return { name: moduleName, isRootTarget: false };
    } else if (!target) {
      return null;
    }

    return this.getNodeModuleNameForTarget(target);
  }

  getExternalSourceForPod = memoize(this.getExternalSourceForPodWithoutCache.bind(this));

  getExternalSourceForPodWithoutCache(pod?: string): { pod: string; source: string } | null {
    if (!pod) {
      return null;
    }

    const results = getFirstExternalSourceForPod(this.podfile, { name: pod });

    // Keep tracing until we get to a development pod with a local file reference.
    const filePath = results?.source[':podspec'] ?? results?.source[':path'] ?? null;

    if (results && filePath) {
      return { pod: results.pod, source: filePath };
    }

    return null;
  }

  private memoizedGetPackageJsonAnyFilePathInModule = memoizeTrigger(
    this.getPackageJsonAnyFilePathInModuleWithoutCache.bind(this)
  );

  /** This can be a path like `/app/node_modules/expo-camera/ios` or `/app/node_modules/react-native-webrtc` depending on where the podspec is. */
  getPackageJsonAnyFilePathInModule(props: { target: string; filePath: string }) {
    return this.memoizedGetPackageJsonAnyFilePathInModule({
      key: props.target,
      args: [props.filePath],
    });
  }

  getPackageJsonAnyFilePathInModuleWithoutCache(filePath: string): Record<string, any> | null {
    if (!this.props.projectRoot || !filePath) {
      return null;
    }

    const nativeProjectRoot = path.join(this.props.projectRoot, 'ios');
    // In the case of the root level podspec file.
    try {
      const rootLevelPkgJsonPath = path.join(nativeProjectRoot, 'package.json');
      return require(rootLevelPkgJsonPath);
    } catch {
      return getPackageJsonForPath(path.join(nativeProjectRoot, filePath));
    }
  }
}

function memoize<T extends (key: string) => any, Ret = ReturnType<T>>(func: T) {
  const cache: Record<string, Ret> = {};
  return function (key: string): Ret {
    if (key in cache) {
      return cache[key];
    }
    const result = func(key);
    cache[key] = result;
    return result;
  };
}

function memoizeTrigger<
  T extends (...args: any[]) => any,
  Args = Parameters<T>,
  Ret = ReturnType<T>
>(func: T): (props: { key: string; args: Args }) => Ret {
  const cache: Record<string, Ret> = {};
  return function ({ key, args }: { key: string; args: Args }): Ret {
    if (key in cache) {
      return cache[key];
    }
    // @ts-ignore
    const result = func(...args);
    cache[key] = result;
    return result;
  };
}

// A list of packages that aren't linked through cocoapods directly.
const knownPackages: Record<string, string> = {
  // Added to ReactCore as a `resource_bundle`
  'React-Core-AccessibilityResources': 'react-native',
  YogaKit: 'react-native',
  // flipper
  'Flipper-DoubleConversion': 'react-native',
  'Flipper-Folly': 'react-native',
  'OpenSSL-Universal': 'react-native',
  FlipperKit: 'react-native',
  Flipper: 'react-native',
  'Flipper-RSocket': 'react-native',
};
